import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { CardTheme } from '@/lib/cardThemes';
import { cardThemes } from '@/lib/cardThemes';

export interface CardData {
  /** Raw Cashu token string (cashuA... or cashuB...) */
  cashuToken: string;
  /** Decoded token amount */
  amount: number;
  /** Mint URL */
  mint: string;
  /** Currency unit */
  unit: string;
  /** Front cover photo URL (object URL or blossom URL) */
  photoUrl: string | null;
  /** Inside-right image URL (opposite the QR code when card is opened) */
  insideImageUrl: string | null;
  /** Greeting message */
  message: string;
  /** Recipient name */
  recipientName: string;
  /** Sender name */
  senderName: string;
  /** Selected theme */
  theme: CardTheme;
  /** Generated QR data URL */
  qrDataUrl: string | null;
}

interface CardContextValue {
  card: CardData;
  step: number;
  setStep: (step: number) => void;
  updateCard: (updates: Partial<CardData>) => void;
  resetCard: () => void;
}

const defaultCard: CardData = {
  cashuToken: '',
  amount: 0,
  mint: '',
  unit: 'sat',
  photoUrl: null,
  insideImageUrl: null,
  message: '',
  recipientName: '',
  senderName: '',
  theme: cardThemes[0],
  qrDataUrl: null,
};

const CardContext = createContext<CardContextValue | null>(null);

export function CardProvider({ children }: { children: ReactNode }) {
  const [card, setCard] = useState<CardData>(defaultCard);
  const [step, setStep] = useState(1);

  const updateCard = useCallback((updates: Partial<CardData>) => {
    setCard(prev => ({ ...prev, ...updates }));
  }, []);

  const resetCard = useCallback(() => {
    setCard(defaultCard);
    setStep(1);
  }, []);

  return (
    <CardContext.Provider value={{ card, step, setStep, updateCard, resetCard }}>
      {children}
    </CardContext.Provider>
  );
}

export function useCard(): CardContextValue {
  const ctx = useContext(CardContext);
  if (!ctx) throw new Error('useCard must be used within a CardProvider');
  return ctx;
}
